import {
  Box,
  Button,
  Divider,
  Grid,
  Hidden,
  lighten,
  List,
  ListItem,
  makeStyles,
  Typography,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useLocation } from '@reach/router';
import { graphql } from 'gatsby';
import { getSrc } from 'gatsby-plugin-image';
import { FileNode } from 'gatsby-plugin-image/dist/src/components/hooks';
import { MDXRenderer } from 'gatsby-plugin-mdx';
import scrollTo from 'gatsby-plugin-smoothscroll';
import React from 'react';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
} from 'react-share';
import Affix from '../../components/affix';
import Link from '../../components/link';
import PageLayout from '../../components/page-layout';
import Section from '../../components/section';
import Seo from '../../components/seo';

export const query = graphql`
  query ($slug: String!) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    allMdx(filter: { fields: { slug: { eq: $slug } } }, limit: 1) {
      edges {
        node {
          body
          excerpt(pruneLength: 200)
          tableOfContents
          frontmatter {
            title
            description
            author
            date(formatString: "YYYY-MM-DD")
            image {
              childImageSharp {
                gatsbyImageData(width: 1000)
              }
            }
          }
        }
        next {
          slug
          frontmatter {
            title
            date(formatString: "YYYY-MM-DD")
          }
        }
        previous {
          slug
          frontmatter {
            title
            date(formatString: "YYYY-MM-DD")
          }
        }
      }
    }
  }
`;

type BlogPostProps = {
  data: {
    site: {
      siteMetadata: {
        siteUrl: string;
      };
    };
    allMdx: {
      edges: Array<{
        node: {
          body: any;
          excerpt: string;
          tableOfContents: {
            items?: Array<{
              url: string;
              title: string;
            }>;
          };
          frontmatter: {
            title: string;
            description: string;
            author: string;
            slug: string;
            date: string;
            image: FileNode;
          };
        };
        next: {
          slug: string;
          frontmatter: {
            title: string;
            date: string;
          };
        };
        previous: {
          slug: string;
          frontmatter: {
            title: string;
            date: string;
          };
        };
      }>;
    };
  };
};

const useStyles = makeStyles(theme => ({
  root: {
    '& .gatsby-highlight': {
      backgroundColor: theme.palette.secondary.main,
    },
    '& :not(pre) > code[class*="language-"]': {
      backgroundColor: lighten(theme.palette.secondary.light, 0.2),
      padding: theme.spacing(0.25, 1),
    },

    '& blockquote': {
      marginInlineStart: 0,
      marginInlineEnd: 0,
      paddingInlineStart: '1.25em',
      paddingInlineEnd: '1em',
      paddingBlockStart: '0.5em',
      paddingBlockEnd: '0.5em',
      borderLeft: `2px solid ${theme.palette.grey[600]}`,
      color: theme.palette.grey[500],
      fontStyle: 'italic',
      fontSize: '1.25rem',

      '& p:first-child': {
        marginBlockStart: 0,
      },

      '& p:last-child': {
        marginBlockEnd: 0,
      },

      '& cite': {
        fontSize: '1rem',
      },
    },
  },

  postMetadata: {
    margin: theme.spacing(1, 0, 2),
    color: theme.palette.grey[400],
    textAlign: 'center',
    display: 'block',
    fontSize: 16,
    fontWeight: 700,
  },

  navLinkWrapper: {
    padding: theme.spacing(4, 0),

    [theme.breakpoints.up('sm')]: {
      '&:last-child': {
        textAlign: 'right',
      },
    },
  },

  navLink: {
    fontWeight: 700,
    fontSize: '1.1rem',
  },

  allPostsLink: {
    '&:hover': {
      textDecoration: 'none',
    },
  },

  allPostsBtn: {
    textDecoration: 'none',
    fontSize: '0.8rem',
    fontWeight: 700,
    color: theme.palette.grey[500],
    marginBottom: theme.spacing(5),
    '&:hover': {
      textDecoration: 'none',
    },
    '&:before': {
      content: '""',
      display: 'none',
    },
  },

  tableOfContentsItem: {
    paddingLeft: 0,
    paddingRight: 0,
  },

  subscribeBox: {
    position: 'fixed',
    right: theme.spacing(2),
    bottom: theme.spacing(2),
  },
}));

const BlogPostLayout: React.FC<BlogPostProps> = props => {
  const classes = useStyles();
  const { siteUrl } = props.data.site.siteMetadata;
  const { title, description, image, author, date } = props.data.allMdx.edges[0].node.frontmatter;
  const { body, excerpt, tableOfContents } = props.data.allMdx.edges[0].node;
  const { next, previous } = props.data.allMdx.edges[0];
  const imageSrc = getSrc(image);

  const { pathname } = useLocation();
  const url = `${siteUrl}${pathname}`;

  const SocialLinks = () => (
    <Box display="flex" justifyContent="center">
      <Box px={1}>
        <EmailShareButton url={url} subject={title}>
          <EmailIcon size={32} round />
        </EmailShareButton>
      </Box>
      <Box px={1}>
        <TwitterShareButton url={url} title={title}>
          <TwitterIcon size={32} round />
        </TwitterShareButton>
      </Box>
      <Box px={1}>
        <LinkedinShareButton url={url} title={title}>
          <LinkedinIcon size={32} round />
        </LinkedinShareButton>
      </Box>
      <Box px={1}>
        <FacebookShareButton url={url} quote={title}>
          <FacebookIcon size={32} round />
        </FacebookShareButton>
      </Box>
    </Box>
  );

  return (
    <PageLayout className={classes.root} showFooterHero={false}>
      <Seo title={title} titleTemplate="%s" description={description || excerpt} image={imageSrc} />

      <Section maxWidth="lg" verticalSpacing={8}>
        <Grid container spacing={4}>
          <Grid item md={tableOfContents.items?.length ? 10 : false} xs={12}>
            <Link to="/blog" className={classes.allPostsLink}>
              <Button className={classes.allPostsBtn} disableRipple startIcon={<ArrowBackIcon />}>
                All posts
              </Button>
            </Link>

            <Typography variant="h1">{title}</Typography>
            <Typography
              component="div"
              variant="overline"
              align="center"
              className={classes.postMetadata}
            >
              {author} - {date}
            </Typography>
            <SocialLinks />

            <MDXRenderer>{body}</MDXRenderer>

            <Divider style={{ marginBottom: 15 }} />

            <SocialLinks />

            <Grid container justifyContent="space-between">
              <Grid item md={6} xs={12} className={classes.navLinkWrapper}>
                {previous && (
                  <div>
                    <Typography variant="overline">Previous</Typography>
                    <div>
                      <Link
                        to={`/blog/${previous.frontmatter.date}/${previous.slug}`}
                        className={classes.navLink}
                      >
                        {previous.frontmatter.title}
                      </Link>
                    </div>
                  </div>
                )}
              </Grid>
              <Grid item md={6} xs={12} className={classes.navLinkWrapper}>
                {next && (
                  <div>
                    <Typography variant="overline">Next</Typography>
                    <div>
                      <Link
                        to={`/blog/${next.frontmatter.date}/${next.slug}`}
                        className={classes.navLink}
                      >
                        {next.frontmatter.title}
                      </Link>
                    </div>
                  </div>
                )}
              </Grid>
            </Grid>
          </Grid>

          {tableOfContents.items?.length && (
            <Hidden smDown>
              <Grid item md={2}>
                <Affix top={70}>
                  <Typography variant="overline">Contents</Typography>

                  <List>
                    {tableOfContents.items.map(item => (
                      <ListItem key={item.url} className={classes.tableOfContentsItem}>
                        <Link onClick={() => scrollTo(item.url)} style={{ cursor: 'pointer' }}>
                          {item.title}
                        </Link>
                      </ListItem>
                    ))}
                  </List>
                </Affix>
              </Grid>
            </Hidden>
          )}
        </Grid>
      </Section>
    </PageLayout>
  );
};

export default BlogPostLayout;
